import React, { useState } from 'react'
import { toast } from 'react-toastify'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    TextField,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CategoryIcon from '@material-ui/icons/Category'

import { Container, LoadingButton } from '../../../components'
import { ProductProfileCreatePayload } from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    open: boolean
    apartmentId: number | null
    onClose: () => void
    onCreate: (payload: ProductProfileCreatePayload) => Promise<void>
}

export const CreateProductProfileModal: React.FC<Props> = ({
    open,
    apartmentId,
    onClose,
    onCreate,
}) => {
    const theme = useTheme()
    const [brand, setBrand] = useState('')
    const [modelNumber, setModelNumber] = useState('')
    const [displayName, setDisplayName] = useState('')
    const [saving, setSaving] = useState(false)

    const handleClose = () => {
        setBrand('')
        setModelNumber('')
        setDisplayName('')
        onClose()
    }

    const handleCreate = async () => {
        if (!apartmentId) {
            toast.error('No active property')
            return
        }
        if (!brand.trim() || !modelNumber.trim()) {
            toast.error('Brand and model number are required')
            return
        }

        setSaving(true)
        try {
            await onCreate({
                apartment_id: apartmentId,
                brand: brand.trim(),
                model_number: modelNumber.trim(),
                display_name: displayName.trim() || undefined,
            })
            handleClose()
        } catch (error: any) {
            const detail =
                error?.response?.data?.detail ??
                'Could not create product profile'
            toast.error(detail)
        } finally {
            setSaving(false)
        }
    }

    const canSubmit = brand.trim() && modelNumber.trim()

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
        >
            {/* Colored header */}
            <Container
                style={{
                    padding: theme.spacing(3, 3, 2.5),
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}02 100%)`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexDirection: 'column',
                    gap: theme.spacing(1.5),
                }}
            >
                <Container
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <Container
                        style={{
                            alignItems: 'center',
                            gap: theme.spacing(1.5),
                        }}
                    >
                        <Container
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: 10,
                                backgroundColor: theme.palette.primary.main,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CategoryIcon
                                style={{
                                    fontSize: 22,
                                    color: '#fff',
                                }}
                            />
                        </Container>
                        <Container
                            style={{
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                }}
                            >
                                Add product
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Create a product profile for your property
                            </span>
                        </Container>
                    </Container>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Container>
            </Container>

            <DialogContent style={{ padding: theme.spacing(3) }}>
                <Container
                    style={{
                        flexDirection: 'column',
                        gap: theme.spacing(3),
                    }}
                >
                    <Container
                        style={{
                            padding: theme.spacing(1.5, 2),
                            borderRadius: 8,
                            backgroundColor: theme.palette.info.main + '08',
                            border: `1px solid ${theme.palette.info.main}20`,
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                                lineHeight: 1.5,
                            }}
                        >
                            Documents you upload will auto-link to all inventory
                            assets across your property that share the same
                            brand and model number.
                        </span>
                    </Container>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                label="Brand"
                                placeholder="e.g. Samsung, GE, Whirlpool"
                                value={brand}
                                onChange={(event) =>
                                    setBrand(event.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                label="Model number"
                                placeholder="e.g. WF45T6000AW"
                                value={modelNumber}
                                onChange={(event) =>
                                    setModelNumber(event.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label="Display name"
                                placeholder="Optional — defaults to Brand + Model"
                                helperText="A friendly label shown in the catalog and on asset cards"
                                value={displayName}
                                onChange={(event) =>
                                    setDisplayName(event.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </DialogContent>
            <DialogActions
                style={{
                    padding: theme.spacing(2, 3),
                    gap: theme.spacing(1),
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Button
                    onClick={handleClose}
                    style={{ textTransform: 'none', fontWeight: 500 }}
                >
                    Cancel
                </Button>
                <LoadingButton
                    loading={saving}
                    color="primary"
                    variant="contained"
                    disabled={!canSubmit}
                    onClick={handleCreate}
                    style={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 8,
                        padding: theme.spacing(1, 3),
                        boxShadow: canSubmit
                            ? `0 2px 8px ${theme.palette.primary.main}40`
                            : 'none',
                    }}
                >
                    Create product
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
